// The standard reference fingering: display data for the scale drill, never
// graded. Fingering is per (scale x tonic), because where the black keys fall
// moves the thumb (C major and B major differ), so it is a table, not a rule
// derived from the formula. A scale, key or hand with no entry shows no
// fingering.
//
// Filled from published scale references (scaleFingeringTable.ts): every entry
// carries the name and page of the references that print it, so a fingering
// can be traced. A hand is filled only where a reference prints it and no
// trusted reference prints otherwise; a hand the references disagree on stays
// empty until it is ruled on. The twelve major scales are the standard method
// book fingerings, both hands.
//
// An entry is one octave ascending, tonic to tonic, as a scale book prints it
// (fingers 1 = thumb to 5). A longer run repeats the octave: the right hand
// keeps the octave's first finger on every inner tonic and ends on its last;
// the left hand starts on its first finger and keeps the octave's last finger
// on every inner tonic (see `runFingers`). The chromatic scale is the one
// exception: it is fingered note by note (3 on every black key, the thumb on
// the white keys, 2 in place of the thumb where two white keys meet), so each
// note of a longer run keeps the finger the rule gives it. A descending run
// plays the same finger on each note.

import type { Tonic } from 'Types/ExerciseContentTypes';
import { scaleFormula } from './scaleFormulas';
import { SCALE_FINGERING_ROWS } from './scaleFingeringTable';

export type Finger = 1 | 2 | 3 | 4 | 5;

/** Whether the reference finger numbers are drawn on a scale run (a display setting). */
export type FingerNumbersDisplay = 'show' | 'hide';

export const FINGER_NUMBERS_DISPLAYS: readonly FingerNumbersDisplay[] = ['show', 'hide'];

export const DEFAULT_FINGER_NUMBERS_DISPLAY: FingerNumbersDisplay = 'show';

export type Hand = 'RH' | 'LH';

/** A published reference that prints a fingering: its name and the page. */
export interface FingeringSource {
  name: string;
  url: string;
}

/** One hand's octave for one (unit, tonic), as the references print it. */
export interface FingeringRow {
  unit: string;
  tonic: Tonic;
  hand: Hand;
  fingers: readonly Finger[];
  sources: readonly FingeringSource[];
}

export interface OctaveFingering {
  /** Right hand, tonic to tonic ascending: one finger per scale note plus the top tonic. */
  rh?: readonly Finger[];
  /** Left hand, the same notes. */
  lh?: readonly Finger[];
  /** The references that print each hand's fingering. */
  sources: { rh?: readonly FingeringSource[]; lh?: readonly FingeringSource[] };
}

const handKey = (hand: Hand): 'rh' | 'lh' => (hand === 'RH' ? 'rh' : 'lh');

const withRow = (
  table: Record<string, Partial<Record<Tonic, OctaveFingering>>>,
  row: FingeringRow,
): Record<string, Partial<Record<Tonic, OctaveFingering>>> => {
  const unitEntries = table[row.unit] ?? {};
  const entry = unitEntries[row.tonic] ?? { sources: {} };
  const key = handKey(row.hand);
  return {
    ...table,
    [row.unit]: {
      ...unitEntries,
      [row.tonic]: {
        ...entry,
        [key]: row.fingers,
        sources: { ...entry.sources, [key]: row.sources },
      },
    },
  };
};

/** Unit id -> tonic -> the octave's fingering. */
export const SCALE_FINGERINGS: Record<
  string,
  Partial<Record<Tonic, OctaveFingering>>
> = SCALE_FINGERING_ROWS.reduce(withRow, {});

/** The octave's fingering for (unit, tonic), or null where none is authored. */
export const fingeringFor = (unitId: string, tonic: Tonic): OctaveFingering | null => {
  const entry = SCALE_FINGERINGS[unitId]?.[tonic];
  if (!entry) return null;
  const notesPerOctave = scaleFormula(unitId).length;
  const spansOctave = (fingers: readonly Finger[] | undefined): boolean =>
    fingers === undefined || fingers.length === notesPerOctave + 1;
  if (!spansOctave(entry.rh) || !spansOctave(entry.lh)) {
    throw new Error(`scale fingering: ${unitId} on ${tonic} does not span one octave`);
  }
  return entry;
};

/**
 * One hand's fingers for an ascending run of `rangeOctaves` octaves of
 * `unitId`, one per note from the bottom tonic to the top:
 * `rangeOctaves * notesPerOctave + 1`.
 */
export const runFingers = (
  octave: readonly Finger[],
  hand: Hand,
  rangeOctaves: number,
  unitId: string,
): Finger[] => {
  if (!Number.isInteger(rangeOctaves) || rangeOctaves < 1) {
    throw new Error(`scale fingering: range ${rangeOctaves} is not a whole number of octaves`);
  }
  const repeats = Array.from({ length: rangeOctaves });
  const firstFingerOnInnerTonics = (): Finger[] => [
    ...repeats.flatMap(() => octave.slice(0, -1)),
    octave[octave.length - 1],
  ];
  const lastFingerOnInnerTonics = (): Finger[] => [
    octave[0],
    ...repeats.flatMap(() => octave.slice(1)),
  ];
  // Chromatic, note by note: an inner tonic is reached from the note below and
  // leads on to the note above, as the octave's top tonic is in the right hand
  // and its bottom tonic in the left.
  if (unitId === 'chromatic') {
    return hand === 'RH' ? lastFingerOnInnerTonics() : firstFingerOnInnerTonics();
  }
  return hand === 'RH' ? firstFingerOnInnerTonics() : lastFingerOnInnerTonics();
};
